import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";

export interface WaitingRoomResponse {
  patients: WaitingRoomItem[];
}

export interface WaitingRoomItem {
  encounter: WaitingRoomEncounter;
  patient: WaitingRoomPatient;
  metadata: WaitingRoomMetadata;
}

export interface WaitingRoomEncounter {
  assigneeProfessionalUserId: number | null;
  condition: string;
  customerWaitingSince?: string;
  id: string;
  isAv: boolean;
  isDfr: boolean;
  isRxRequest: boolean | null;
  isRxRenewalRequest: boolean | null;
  isAsyncDxRequest: boolean | null;
  isBridge: boolean;
  isFollowUp: boolean;
  isHandOff: boolean;
  isInProgress: boolean;
  isIncomplete: boolean;
  priorityLevel: string;
  programType?: string | null;
  rfe: string;
  roomType: WaitingRoomType;
  slaBreachTime?: string;
  slaWeight: number;
  suggestedPractitionerName: string | null;
  suggestedProfessionalUserId: number | null;
  tempAddressState: string | null;
}

export interface WaitingRoomPatient {
  addressState: string;
  administrativeGender: string;
  age: number;
  dob: string;
  genderIdentity: string;
  id: string;
  isPeds: boolean;
  nameFirst: string;
  nameLast: string;
  partnerUids: string[];
  preferredPartnerUid?: string | null;
}

export interface WaitingRoomMetadata {
  canOverrideLicense: boolean;
}

export enum WaitingRoomType {
  CA = "ca",
  CLINICIAN = "clinician",
}

export interface WaitingRoomDashboardParams {
  mineOnly?: boolean;
  abridged?: boolean;
}

export const useWaitingRoomDashboardDashboard = (
  params: WaitingRoomDashboardParams = {},
  enabled = true,
) => {
  const { mineOnly = false, abridged = false } = params;

  return useApi<WaitingRoomResponse>({
    queryOptions: {
      queryFn: async () => {
        const queryParams: Record<string, boolean> = {};
        if (mineOnly) queryParams.mineOnly = true;
        if (abridged) queryParams.abridged = true;

        const response = await axiosInstance.get<WaitingRoomResponse>(
          "/new-api/waiting-room-dashboard",
          { params: queryParams },
        );

        return response.data;
      },
      enabled,
      queryKey: QueryKeys.Provider.LiveQueueDashboard.WITH_PARAMS({
        mineOnly,
        abridged,
      }),
    },
  });
};
